/**
 * CreateRoleModal (S2.3) — the SINGLE clone-preset modal: name + clone-from +
 * `inheritsFrom` multi-select. Deliberately NOT a wizard (plan §Slice 2.3): the
 * fleet 3-step CreateRoleWizard's permission/user steps duplicated RolesTab's
 * matrix (S2.2) and UsersTab's assignment (S2.4) — the new role opens straight
 * into those existing surfaces to set grants and members instead.
 *
 * Least-privilege defaults: the `isSystem` admin role is EXCLUDED from both the
 * clone-from and inherits-from options — a role holding ALL_PERMISSIONS is
 * minted only by assigning `role-admin` itself, never silently via an edge or a
 * clone. A NEW role cannot close an inheritance cycle (nothing points at it),
 * so the acyclic guard for creation lives server-side only (shared
 * `findRoleCycle` definition, defence-in-depth).
 */

import { useMemo } from 'react';
import { App, Input, Modal, Select, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { useRoleHierarchyMutations } from './useRoleHierarchy';
import type { GovernanceRole } from './model';

const { Text } = Typography;

interface CreateRoleFormData {
  name: string;
  cloneFromRoleId?: string;
  inheritsFrom: string[];
}

export interface CreateRoleModalProps {
  open: boolean;
  /** The current merged role catalog (the panel's list). */
  roles: GovernanceRole[];
  onCancel: () => void;
  /** Fired after a successful create — the panel routes on to the matrix. */
  onCreated: (role: GovernanceRole) => void;
}

const CreateRoleModal = ({ open, roles, onCancel, onCreated }: CreateRoleModalProps) => {
  const { t } = useTranslation('governance');
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { message } = App.useApp();
  const { createRole } = useRoleHierarchyMutations();

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('createRole.nameRequired')),
        cloneFromRoleId: z.string().optional(),
        inheritsFrom: z.array(z.string()),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<CreateRoleFormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', cloneFromRoleId: undefined, inheritsFrom: [] },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  /** Active, non-system roles — the sanctioned clone/inherit sources. */
  const roleOptions = useMemo(
    () =>
      roles
        .filter((role) => role.status === 'active' && !role.isSystem)
        .map((role) => ({ label: role.name, value: role.id })),
    [roles]
  );

  const close = () => {
    reset({ name: '', cloneFromRoleId: undefined, inheritsFrom: [] });
    onCancel();
  };

  const onSubmit = handleSubmit(async (data) => {
    const role = await createRole({
      name: data.name.trim(),
      ...(data.cloneFromRoleId ? { cloneFromRoleId: data.cloneFromRoleId } : {}),
      inheritsFrom: data.inheritsFrom,
    });
    message.success(t('createRole.created', { name: role.name }));
    reset({ name: '', cloneFromRoleId: undefined, inheritsFrom: [] });
    onCreated(role);
  });

  return (
    <Modal
      open={open}
      title={t('createRole.title')}
      okText={t('createRole.submit')}
      cancelText={t('actions.cancel')}
      onCancel={close}
      onOk={onSubmit}
      confirmLoading={isSubmitting}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      maskClosable={false}
      destroyOnHidden
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginSM }}>
        <div>
          <Text style={formStyles.labelStyle}>{t('createRole.name')}</Text>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                size="large"
                style={formStyles.inputStyle}
                status={errors.name && touchedFields.name ? 'error' : undefined}
              />
            )}
          />
          {errors.name && touchedFields.name && (
            <Text style={formStyles.errorStyle}>{errors.name.message}</Text>
          )}
        </div>

        <div>
          <Text style={formStyles.labelStyle}>{t('createRole.cloneFrom')}</Text>
          <Controller
            name="cloneFromRoleId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                size="large"
                style={{ width: '100%' }}
                allowClear
                placeholder={t('createRole.cloneFromNone')}
                options={roleOptions}
              />
            )}
          />
        </div>

        <div>
          <Text style={formStyles.labelStyle}>{t('createRole.inheritsFrom')}</Text>
          <Controller
            name="inheritsFrom"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                mode="multiple"
                size="large"
                style={{ width: '100%' }}
                placeholder={t('hierarchy.none')}
                options={roleOptions}
              />
            )}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: token.marginXXS }}>
            {t('createRole.inheritsFromHint')}
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default CreateRoleModal;
